import { useEffect, useState } from "react";
import axios from "axios";
import { Star, Quote } from "lucide-react";
import { Reveal, MaskLines } from "./Motion";
import { API } from "@/lib/api";

const GoogleG = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 48 48" className={className} aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const Stars = ({ n = 5, size = "h-4 w-4" }) => (
  <span className="flex gap-0.5" aria-label={`${n} étoiles sur 5`}>
    {[1, 2, 3, 4, 5].map((k) => <Star key={k} className={`${size} ${k <= Math.round(n) ? "fill-gold text-gold" : "text-paper-line"}`} />)}
  </span>
);

export const Reviews = () => {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/reviews`).then((r) => { if (alive) setData(r.data); }).catch(() => { if (alive) setData(false); });
    return () => { alive = false; };
  }, []);
  const reviews = Array.isArray(data?.reviews) ? data.reviews : [];
  const rating = data?.rating ?? 5;
  const total = data?.total ?? 0;
  if (data === false) return null;

  return (
    <section id="avis" data-testid="reviews-section" className="bg-paper px-5 py-24 sm:px-8 lg:py-32">
      <div className="mx-auto grid max-w-[1400px] gap-14 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <div className="lg:sticky lg:top-28">
            <p className="eyebrow text-gold-deep mb-6">Avis clients</p>
            <h2 className="font-display text-4xl sm:text-5xl leading-[1.02]">
              <MaskLines lines={["Ils nous ont", <em key="e">confié leur intérieur.</em>]} />
            </h2>
            <Reveal className="mt-10 border border-paper-line bg-white/60 p-7" testId="reviews-summary">
              <div className="flex items-center gap-3"><GoogleG /><span className="text-sm font-medium">Avis Google</span></div>
              <div className="mt-5 flex items-end gap-4">
                <span className="font-display text-7xl leading-none" data-testid="reviews-rating">{Number(rating).toFixed(1).replace(".", ",")}</span>
                <div className="pb-2"><Stars n={rating} size="h-5 w-5" /><p className="mt-1 text-sm text-slate-600" data-testid="reviews-total">{total} avis vérifiés</p></div>
              </div>
              {data?.url && (
                <a href={data.url} target="_blank" rel="noopener noreferrer" data-testid="reviews-google-link" className="mt-6 inline-flex link-line text-sm font-semibold">
                  Voir tous nos avis sur Google
                </a>
              )}
            </Reveal>
          </div>
        </div>
        <div className="lg:col-span-8 columns-1 md:columns-2 gap-5 [column-fill:_balance]">
          {!data && [0, 1, 2, 3].map((k) => <div key={k} className="mb-5 h-56 animate-pulse bg-paper-deep break-inside-avoid" />)}
          {reviews.map((r, k) => (
            <Reveal key={k} delay={(k % 2) * 0.1} className="mb-5 break-inside-avoid" testId={`review-card-${k}`}>
              <article className="group relative border border-paper-line bg-white p-7 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(17,22,28,0.35)]">
                <Quote className="absolute right-6 top-6 h-8 w-8 text-paper-deep transition-colors duration-500 group-hover:text-gold" />
                <Stars n={r?.rating ?? 5} />
                <p className="mt-5 text-[15px] leading-relaxed text-slate-700">{r?.text}</p>
                <footer className="mt-6 flex items-center gap-3 border-t border-paper-line pt-5">
                  <span className="flex h-10 w-10 items-center justify-center rounded-full bg-ink font-display text-lg text-gold">{(r?.author || "C")[0]}</span>
                  <span>
                    {r?.author_url
                      ? <a href={r.author_url} target="_blank" rel="noopener noreferrer" className="block text-sm font-semibold hover:text-gold-deep">{r?.author}</a>
                      : <span className="block text-sm font-semibold">{r?.author}</span>}
                    <span className="block text-xs text-slate-600">{[r?.city, r?.when].filter(Boolean).join(" · ")}</span>
                  </span>
                  <GoogleG className="ml-auto h-4 w-4" />
                </footer>
              </article>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
